import { type CSSProperties, createElement, type ReactNode } from 'react';

import classNames from 'classnames';
import classnames from 'classnames';

import GDPRCompliance from '../../signupElement/components/GDPRCompliance';

import type { Price, StripeCardElementComponent, Tier } from '../../types';
import { input, inputSubmit } from '../Pricing.css';

interface Props {
  email?: string;
  onEmailChange: (value: string) => void;
  taxId: string;
  onTaxIdChange: (value: string) => void;
  ctaText: string;
  isCtaDisabled: boolean;
  showTaxIdField: boolean;
  StripeCardElement?: StripeCardElementComponent;
  selectedTier?: Tier;
  selectedPrice?: Price;
  PaymentRequestButtonElement?: ReactNode;
  hasTermsOfService?: boolean;
  hasCustomTermsOfService?: string;
  inputTextColor?: CSSProperties['color'];
  inputPlaceholderColor?: CSSProperties['color'];
  inputBackgroundColor?: CSSProperties['backgroundColor'];
}

const InternalCheckout = ({
  email,
  onEmailChange,
  taxId,
  onTaxIdChange,
  isCtaDisabled,
  StripeCardElement,
  selectedTier,
  selectedPrice,
  ctaText = 'Upgrade',
  showTaxIdField = false,
  PaymentRequestButtonElement,
  hasTermsOfService = false,
  hasCustomTermsOfService,
  inputTextColor,
  inputPlaceholderColor,
  inputBackgroundColor,
}: Props) => {
  if (!selectedTier || !selectedPrice) {
    return null;
  }

  const emailField = (
    <input
      name="email"
      defaultValue={email}
      onBlur={(e) => e.currentTarget.value && onEmailChange(e.currentTarget.value)}
      type="email"
      placeholder="Email"
      className={classnames('border py-2 px-4 rounded-md', input)}
    />
  );

  const gdprField = hasTermsOfService && (
    <GDPRCompliance hasCustomTermsOfService={hasCustomTermsOfService} defaultTOSConsentValue={true} />
  );

  if (PaymentRequestButtonElement) {
    return (
      <div className="flex flex-col gap-2 max-w-md w-full">
        {emailField}
        {PaymentRequestButtonElement}
      </div>
    );
  }

  if (selectedTier?.id === 'free') {
    return (
      <div className="flex flex-col gap-2 max-w-md w-full">
        {emailField}
        {gdprField}
        <input
          disabled={isCtaDisabled}
          type="submit"
          value="Continue as free reader"
          className={classNames(inputSubmit, 'rounded-md py-2 px-4')}
        />
      </div>
    );
  }

  if (selectedPrice?.premium_offer && !selectedPrice?.premium_offer.card_required) {
    return (
      <div className="flex flex-col gap-2 max-w-md w-full">
        {emailField}
        {gdprField}
        <input
          disabled={isCtaDisabled}
          type="submit"
          value={ctaText}
          className={classNames(inputSubmit, 'rounded-md py-2 px-4')}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 max-w-md w-full">
      {emailField}
      {StripeCardElement ? (
        createElement(StripeCardElement, {
          className: classNames('border py-3 px-4 rounded-md', input),
          options: {
            style: {
              base: {
                backgroundColor: inputBackgroundColor,
                fontSize: '14px',
                color: inputTextColor,
                '::placeholder': {
                  color: inputPlaceholderColor,
                },
              },
            },
          },
        })
      ) : (
        <input
          type="text"
          placeholder="Credit Card Placeholder"
          className={classNames('border py-2 px-4 rounded-md', input)}
        />
      )}
      {showTaxIdField && (
        <input
          type="text"
          name="tax_id"
          placeholder="Tax ID"
          defaultValue={taxId}
          onBlur={(e) => onTaxIdChange(e.target.value)}
          className={classnames('border py-2 px-4 rounded-md', input)}
        />
      )}
      <input
        disabled={isCtaDisabled}
        type="submit"
        value={ctaText}
        className={classNames(inputSubmit, 'rounded-md py-2 px-4')}
      />
    </div>
  );
};

export default InternalCheckout;
